import { useEffect, useRef, useState } from "react"
import { ENV } from "~/ENV"
import { parseThumbnailsVtt, resolveThumbnailsUrl, type ThumbnailCue } from "../lib/thumbnailsVtt"

const NO_CUES: readonly ThumbnailCue[] = []

/**
 * Loads a session's scrub-preview index.
 *
 * Cached per URL for the life of the page, because the session rail makes it
 * ordinary to move between sessions and come back, and the VTT for a long video
 * is not small. Failure is deliberately quiet: no thumbnails is a smaller loss
 * than an error state over a video that is playing fine.
 */
export const useThumbnailCues = (thumbnailsUrl: string | undefined): readonly ThumbnailCue[] => {
  const [cues, setCues] = useState<readonly ThumbnailCue[]>(NO_CUES)
  const cacheRef = useRef(new Map<string, readonly ThumbnailCue[]>())

  useEffect(() => {
    const resolved = resolveThumbnailsUrl(thumbnailsUrl, ENV.CDN_URL)

    if (!resolved) {
      setCues(NO_CUES)
      return
    }

    const cached = cacheRef.current.get(resolved)
    if (cached) {
      setCues(cached)
      return
    }

    const controller = new AbortController()
    const cache = cacheRef.current

    void fetch(resolved, { signal: controller.signal })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load thumbnails VTT: ${response.status}`)
        }

        return response.text()
      })
      .then(text => {
        const parsed = parseThumbnailsVtt(text)
        cache.set(resolved, parsed)
        setCues(parsed)
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setCues(NO_CUES)
        }
      })

    return () => controller.abort()
  }, [thumbnailsUrl])

  return cues
}
